import { useState } from "react";
import { preformatMakeCredReq } from "../utils/webauthn";
import {
  useRegisterMfaMutation,
  useSendAuthnResponseMutation,
} from "../services/idp/idp";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { RegistrationResponse, AuthnResponse } from "../services/idp/idp.types";
import { formatMessageFromResponseErrorData } from "../utils/errors";

export const useRegister = (): {
  register: (
    otp: string,
    nickname: string,
  ) => Promise<void | {
    data: AuthnResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  error: string;
  otpRegistered: boolean;
} => {
  const [error, setError] = useState("");
  const [otpRegistered, setOtpRegistered] = useState(false);
  const [registration, preRegState] = useRegisterMfaMutation();
  const [authn, regState] = useSendAuthnResponseMutation();

  const register = (otp: string, nickname: string) => {
    setError("");
    return registration()
      .unwrap()
      .then((response: RegistrationResponse) => {
        const publicKey = preformatMakeCredReq(response.publicKey);
        return navigator.credentials.create({ publicKey }).catch((err) => {
          err.isWebAuthnError = true;
          throw err;
        });
      })
      .then((response: any) => {
        const c = response;
        const pKey = c.response.getPublicKey();
        const pKeyAlg = c.response.getPublicKeyAlgorithm();
        const authData = c.response.getAuthenticatorData();
        const transports =
          typeof c.response.getTransports === "function"
            ? c.response.getTransports()
            : (c.response.transports ?? []);

        const credential = {
          authenticatorAttachment: c.authenticatorAttachment,
          id: c.id,
          rawId: bufferToBase64url(c.rawId),
          response: {
            attestationObject: bufferToBase64url(c.response.attestationObject),
            clientDataJSON: bufferToBase64url(c.response.clientDataJSON),
            publicKey: bufferToBase64url(pKey),
            publicKeyAlgorithm: pKeyAlg,
            authenticatorData: bufferToBase64url(authData),
            transports: transports,
          },
          type: c.type,
        };
        return authn({ credential, otp, nickname });
      })
      .then((response: any) => {
        if (response.error) {
          setError(formatMessageFromResponseErrorData(response.error?.data));
        } else if (response.data) {
          setOtpRegistered(response.data.otpRegistered);
        }
        return response;
      })
      .catch((error: any) => {
        console.error({ error });
        let message =
          error.data?.detail ||
          error.message ||
          formatMessageFromResponseErrorData(error.data);
        if (error.isWebAuthnError) {
          const browserExtensionHint =
            "If you have browser extensions that may block WebAuthn requests (e.g. ad blockers, privacy extensions), try disabling them and attempt registration again.";
          message = `${message}\n${browserExtensionHint}`;
        }
        setError(message);
        return error;
      });
  };

  return {
    register,
    error,
    otpRegistered,
    isError: preRegState.isError || regState.isError,
    isSuccess: regState.isSuccess,
    isLoading: preRegState.isLoading || regState.isLoading,
  };
};

function bufferToBase64url(buffer: any) {
  // modified from https://github.com/github/webauthn-json/blob/main/src/webauthn-json/base64url.ts

  const byteView = new Uint8Array(buffer);
  let str = "";
  for (const charCode of byteView) {
    str += String.fromCharCode(charCode);
  }

  // Binary string to base64
  const base64String = btoa(str);

  // Base64 to base64url
  // We assume that the base64url string is well-formed.
  const base64urlString = base64String
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
  return base64urlString;
}
